import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import styles from '../styles/Navbar.module.css';
import logoImage from '../resources/logo.png'; // Make sure to replace this with the correct path to your logo image

const Navbar: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const navbarRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (navbarRef.current && !navbarRef.current.contains(event.target as Node)) {
        setActiveDropdown(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    setIsOpen(false);
    setActiveDropdown(null);
  }, [location]);

  const toggleDropdown = (name: string) => {
    setActiveDropdown(activeDropdown === name ? null : name);
  };

  const isActive = (path: string) => {
    return location.pathname === path ? styles.active : '';
  };

  // Function to handle section navigation
  const scrollToSection = (sectionId: string) => {
    if (location.pathname !== '/programs') {
      navigate(`/programs#${sectionId}`);
    } else {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  // Function to handle awards section navigation
  const navigateToAwardsSection = (sectionId: string) => {
    if (location.pathname !== '/AwardsPage') {
      navigate(`/AwardsPage#${sectionId}`);
    } else {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
    setIsOpen(false);
  };

  return (
    <nav className={`${styles.navbar} ${isScrolled ? styles.scrolled : ''}`} ref={navbarRef}>
      <div className={styles['navbar-container']}>
        <div className={`${styles.logo} ${isScrolled ? styles.scrolledLogo : ''}`}>
          <Link to="/">
            <img src={logoImage} alt="Philosophy Department Logo" className={styles['logo-image']} />
          </Link>
        </div>

        <div
          className={`${styles.hamburger} ${isOpen ? styles.active : ''}`}
          onClick={() => setIsOpen(!isOpen)}
        >
          <span className={styles.bar}></span>
          <span className={styles.bar}></span>
          <span className={styles.bar}></span>
        </div>

        <ul className={`${styles['nav-links']} ${isOpen ? styles.active : ''}`}>
          <li className={isActive('/AwardsPage')}>
            <Link to="/AwardsPage#awards" onClick={() => setIsOpen(false)}>Awards</Link>
          </li>

          {/* About Us with dropdown */}
          <li
            className={`${styles['has-dropdown']} ${activeDropdown === 'about' ? styles.active : ''}`}
            onMouseEnter={() => window.innerWidth > 992 && setActiveDropdown('about')}
            onMouseLeave={() => window.innerWidth > 992 && setActiveDropdown(null)}
          >
            <div className={styles['dropdown-toggle']} onClick={() => toggleDropdown('about')}>
              <span>About Us</span>
              <span className={styles.dropdownIcon}>+</span>
            </div>
            <ul className={styles.dropdown}>
              <li><Link to="/about" onClick={() => setIsOpen(false)}>Overview</Link></li>
              <li><Link to="/about#mission" onClick={() => setIsOpen(false)}>Mission & Vision</Link></li>
              <li><Link to="/about#history" onClick={() => setIsOpen(false)}>History</Link></li>
              <li><Link to="/about#faculty" onClick={() => setIsOpen(false)}>Faculty Directory</Link></li>
              <li>
                <a onClick={() => navigateToAwardsSection('awards')}>
                  Awards & Recognition
                </a>
              </li>
            </ul>
          </li>

          {/* Programs with dropdown */}
          <li
            className={`${styles['has-dropdown']} ${activeDropdown === 'programs' ? styles.active : ''}`}
            onMouseEnter={() => window.innerWidth > 992 && setActiveDropdown('programs')}
            onMouseLeave={() => window.innerWidth > 992 && setActiveDropdown(null)}
          >
            <div className={styles['dropdown-toggle']} onClick={() => toggleDropdown('programs')}>
              <span>Programs</span>
              <span className={styles.dropdownIcon}>+</span>
            </div>
            <ul className={styles.dropdown}>
              <li><Link to="/programs" onClick={() => setIsOpen(false)}>All Programs</Link></li>
              <li><a onClick={() => scrollToSection('undergraduate')}>Undergraduate</a></li>
              <li><a onClick={() => scrollToSection('graduate')}>Graduate</a></li>
              <li><a onClick={() => scrollToSection('phd')}>Doctoral</a></li>
              <li><a onClick={() => scrollToSection('certificates')}>Certificates</a></li>
              <li><a onClick={() => scrollToSection('admission')}>Admission Process</a></li>
            </ul>
          </li>

          <li className={isActive('/news')}>
            <Link to="/news" onClick={() => setIsOpen(false)}>News</Link>
          </li>

          {/* Events with dropdown */}
          <li
            className={`${styles['has-dropdown']} ${activeDropdown === 'events' ? styles.active : ''}`}
            onMouseEnter={() => window.innerWidth > 992 && setActiveDropdown('events')}
            onMouseLeave={() => window.innerWidth > 992 && setActiveDropdown(null)}
          >
            <div className={styles['dropdown-toggle']} onClick={() => toggleDropdown('events')}>
              <span>Events</span>
              <span className={styles.dropdownIcon}>+</span>
            </div>
            <ul className={styles.dropdown}>
              <li><Link to="/events" onClick={() => setIsOpen(false)}>All Events</Link></li>
              <li><Link to="/events#lectures" onClick={() => setIsOpen(false)}>Guest Lectures</Link></li>
              <li><Link to="/events#conferences" onClick={() => setIsOpen(false)}>Conferences</Link></li>
              <li><Link to="/events#workshops" onClick={() => setIsOpen(false)}>Workshops</Link></li>
              <li><Link to="/events#calendar" onClick={() => setIsOpen(false)}>Event Calendar</Link></li>
            </ul>
          </li>

          {/* Resources with dropdown */}
          <li
            className={`${styles['has-dropdown']} ${activeDropdown === 'resources' ? styles.active : ''}`}
            onMouseEnter={() => window.innerWidth > 992 && setActiveDropdown('resources')}
            onMouseLeave={() => window.innerWidth > 992 && setActiveDropdown(null)}
          >
            <div className={styles['dropdown-toggle']} onClick={() => toggleDropdown('resources')}>
              <span>Resources</span>
              <span className={styles.dropdownIcon}>+</span>
            </div>
            <ul className={styles.dropdown}>
              <li><Link to="/CourseCatalog" onClick={() => setIsOpen(false)}>Course Catalog</Link></li>
              <li><Link to="/CourseCatalog" onClick={() => setIsOpen(false)}>Student Association</Link></li>
              <li><Link to="/CourseCatalog" onClick={() => setIsOpen(false)}>Faculty Resources</Link></li>
              <li><Link to="/CourseCatalog" onClick={() => setIsOpen(false)}>Career Services</Link></li>
            </ul>
          </li>

          {/* Staff with dropdown (now including Research as direct items) */}
          <li
            className={`${styles['has-dropdown']} ${activeDropdown === 'staff' ? styles.active : ''}`}
            onMouseEnter={() => window.innerWidth > 992 && setActiveDropdown('staff')}
            onMouseLeave={() => window.innerWidth > 992 && setActiveDropdown(null)}
          >
            <div className={styles['dropdown-toggle']} onClick={() => toggleDropdown('staff')}>
              <span>Staff</span>
              <span className={styles.dropdownIcon}>+</span>
            </div>
            <ul className={styles.dropdown}>
              <li><Link to="/StaffProfile" onClick={() => setIsOpen(false)}>Staff Profiles</Link></li>
              <li><Link to="/StaffDashboard" onClick={() => setIsOpen(false)}>Staff Dashboard</Link></li>
              <li><Link to="/staff/login" onClick={() => setIsOpen(false)}>Staff Login</Link></li>

              {/* Research items directly under Staff */}
              <li><Link to="/research#publications" onClick={() => setIsOpen(false)}>Publications</Link></li>
              <li><Link to="/AdminPage" onClick={() => setIsOpen(false)}>Admin</Link></li>
              <li><Link to="/research#grants" onClick={() => setIsOpen(false)}>Grants & Funding</Link></li>
            </ul>
          </li>

          <li className={isActive('/contact')}>
            <Link to="/contact" onClick={() => setIsOpen(false)}>Contact</Link>
          </li>
        </ul>
      </div>
    </nav>
  );
};

export default Navbar;